import React from "react";
import { defineMessage, useIntl } from "react-intl";
import { SankeyAuto } from "../charts-motion/sankey";
import { withFigureIO } from "../components/figure";
import { useConfig } from "../config";
import { metadata } from "../data/data_finance_fig_AID_FLOWS_SSA";
import { datasets } from "../data/data_finance_fig_AID_FLOWS_SSA_datasets";
import { type ChartRenderer, getAidFlowName } from "../domain";
import * as M from "../materials";
import { computeSankeyNodes, Data, type NodeGroup } from "./AID_FLOWS";

export * from "../data/data_finance_fig_AID_FLOWS_SSA";

export const Chart = ({ data }: ChartRenderer<Data>) => {
  const { aidflows } = useConfig();
  const intl = useIntl();

  const nodeGroups: NodeGroup<Data[number]>[] = React.useMemo(
    () => [
      {
        nodes: "donor",
        depth: 0,
        label: intl.formatMessage(defineMessage({ id: "Donors", defaultMessage: "Donors" })),
        category: "donors",
      },
      {
        nodes: "recipient",
        depth: 1,
        label: intl.formatMessage(defineMessage({ id: "Recipients", defaultMessage: "Recipients" })),
        category: "recipients",
      },
      {
        nodes: "level",
        depth: 2,
        label: intl.formatMessage(defineMessage({ id: "Education levels", defaultMessage: "Education levels" })),
        category: "education_levels",
      },
    ],
    [intl],
  );

  const colorPalettes = React.useMemo(
    () => [
      {
        label: intl.formatMessage(defineMessage({ id: "Donors", defaultMessage: "Donors" })),
        palette: M.q06Palette,
        id: "donors",
      },
      {
        label: intl.formatMessage(defineMessage({ id: "Recipients", defaultMessage: "Recipients" })),
        palette: M.q02Palette,
        id: "recipients",
      },
      {
        label: intl.formatMessage(defineMessage({ id: "Education levels", defaultMessage: "Education levels" })),
        palette: M.q10Palette,
        id: "education_levels",
      },
    ],
    [intl],
  );

  const sankeyData = React.useMemo(() => {
    const localizedData = data.map((row) => ({
      ...row,
      donorId: row.donor,
      donor: getAidFlowName(aidflows, "donor", row.donor),
      recipientId: row.recipient,
      recipient: getAidFlowName(aidflows, "recipient", row.recipient),
      levelId: row.level,
      level: getAidFlowName(aidflows, "level", row.level),
    }));
    return computeSankeyNodes(localizedData, nodeGroups);
  }, [aidflows, data, nodeGroups]);

  return (
    <SankeyAuto data={sankeyData} nodeGroups={nodeGroups} height={M.chartHeight.l} colorPalettes={colorPalettes} />
  );
};

export default withFigureIO({
  url: new URL("../data/data_finance_fig_AID_FLOWS_SSA.json", import.meta.url).href,
  csv: new URL("../data/data_finance_fig_AID_FLOWS_SSA.zip", import.meta.url).href,
  xlsx: new URL("../data/data_finance_fig_AID_FLOWS_SSA.xlsx", import.meta.url).href,
  metadata,
  Data,
  Chart,
  datasets,
});
